import {
  sanitizeRangeInput,
  type MultiSelections,
  type PageItem,
  type PickedProduct,
  type QuotableProduct,
  type RangeSelection,
  type RangeSelections,
  type ShopifyFilterGroup,
  type ShopifyFilterOption,
  type ShopifySearchProduct,
} from './search-state'

const STATIC_FILTER_ORDER = ['type', 'manufacturer', 'brand', 'price']
const CANNED_FILTER_KEYS = new Set(['collection'])
const CANNED_FILTER_LABELS = new Set(['collection'])
const UNIT_LABELS: Record<string, string> = {
  hz: 'Hz',
  hertz: 'Hz',
  gpm: 'GPM',
  lpm: 'LPM',
  lb: 'lb',
  lbs: 'lb',
  inch: 'Inches',
  inches: 'Inches',
  in: 'Inches',
  amp: 'Amps',
  amps: 'Amps',
  volt: 'Volts',
  volts: 'Volts',
  v: 'V',
  btu: 'BTU',
  btus: 'BTU',
}

export function getImageUrl(
  images?: Array<{
    hidden?: boolean
    public_url?: string | null
    store_url?: string | null
  }>,
) {
  const img = images?.find((i) => !i.hidden && (i.public_url || i.store_url))
  return img?.public_url || img?.store_url || null
}

export function selectedRange(min?: string, max?: string): RangeSelection | undefined {
  if (!min && !max) return undefined
  return {
    ...(min ? { min } : {}),
    ...(max ? { max } : {}),
  }
}

export function formatProductSearchCount(value: number | null | undefined) {
  return (value ?? 0).toLocaleString()
}

export function formatMatchingProductsCopy(total?: number | null) {
  return `${formatProductSearchCount(total)} matching products`
}

export function formatProductResultsCopy(total?: number | null) {
  return `${formatProductSearchCount(total)} results`
}

export function buildCompactPageItems(currentPage: number, totalPages: number): PageItem[] {
  const safeTotal = Math.max(1, Math.floor(totalPages))
  const current = Math.min(Math.max(1, Math.floor(currentPage)), safeTotal)
  if (safeTotal <= 7) return Array.from({ length: safeTotal }, (_, idx) => idx + 1)

  const pages = new Set([1, safeTotal, current, current - 1, current + 1])
  if (current <= 3) {
    pages.add(2)
    pages.add(3)
    pages.add(4)
  }
  if (current >= safeTotal - 2) {
    pages.add(safeTotal - 1)
    pages.add(safeTotal - 2)
    pages.add(safeTotal - 3)
  }

  const sorted = [...pages]
    .filter((pageNumber) => pageNumber >= 1 && pageNumber <= safeTotal)
    .sort((a, b) => a - b)
  const items: PageItem[] = []
  sorted.forEach((pageNumber, idx) => {
    const previous = sorted[idx - 1]
    if (previous != null && pageNumber - previous > 1) {
      items.push(previous === 1 ? 'ellipsis-left' : 'ellipsis-right')
    }
    items.push(pageNumber)
  })
  return items
}

export function optionAvailability(option: ShopifyFilterOption) {
  return option.available_count ?? option.count ?? null
}

export function hasActiveContextFilters(
  multiSelections: MultiSelections,
  rangeSelections: RangeSelections,
) {
  return (
    Object.values(multiSelections).some((values) => values.length > 0) ||
    Object.values(rangeSelections).some((range) => range.min || range.max)
  )
}

export function activeFilterCount(
  multiSelections: MultiSelections,
  rangeSelections: RangeSelections,
) {
  return (
    Object.values(multiSelections).reduce((sum, values) => sum + values.length, 0) +
    Object.values(rangeSelections).reduce(
      (sum, range) => sum + (range.min ? 1 : 0) + (range.max ? 1 : 0),
      0,
    )
  )
}

export function isSpecFilterGroup(group: ShopifyFilterGroup) {
  return group.key.startsWith('spec.')
}

export function numberFromRangeValue(value: string | number | null | undefined) {
  // Check emptiness before sanitizing: nonempty inputs such as '$' still become zero.
  if (value == null || value === '') return null
  const number = Number(sanitizeRangeInput(String(value)))
  return Number.isFinite(number) ? number : null
}

export function formatRangeNumber(value: string | number | null | undefined) {
  const number = numberFromRangeValue(value)
  if (number == null) return ''
  const abs = Math.abs(number)
  const maximumFractionDigits = abs !== 0 && abs < 1 ? 3 : 2
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(number)
}

export function formatRangeUnit(unit: string | null | undefined) {
  const trimmed = unit?.trim()
  if (!trimmed) return ''
  return UNIT_LABELS[trimmed.toLowerCase()] ?? trimmed
}

export function formatRangeDisplayValue(
  group: ShopifyFilterGroup,
  value: string | number | null | undefined,
  { includeUnit = false }: { includeUnit?: boolean } = {},
) {
  const formattedNumber = formatRangeNumber(value)
  if (!formattedNumber) return ''
  const prefix = group.key === 'price' ? '$' : ''
  const unit = includeUnit && group.key !== 'price' ? formatRangeUnit(group.unit) : ''
  return `${prefix}${formattedNumber}${unit ? ` ${unit}` : ''}`
}

export function formatFilterOptionLabel(option: ShopifyFilterOption, group: ShopifyFilterGroup) {
  const source = option.label || option.value
  if (!isSpecFilterGroup(group)) return source
  const unit = formatRangeUnit(group.unit)
  const numericWithUnit = source.match(/^\s*(-?\d+(?:\.\d+)?)(?:\s+([a-zA-Z]+))?\s*$/)
  if (!numericWithUnit) return source
  const normalizedUnit = formatRangeUnit(numericWithUnit[2] ?? unit)
  const formattedNumber = formatRangeNumber(numericWithUnit[1])
  return `${formattedNumber}${normalizedUnit ? ` ${normalizedUnit}` : ''}`
}

export function normalizedStaticFilterKey(group: ShopifyFilterGroup) {
  const label = group.label.trim().toLowerCase()
  const key = group.key.trim().toLowerCase()
  if (
    label === 'type' ||
    label === 'product type' ||
    label === 'category' ||
    key === 'type' ||
    key === 'product_type' ||
    key === 'store_taxonomy' ||
    key === 'taxonomy' ||
    key === 'category'
  )
    return 'type'
  if (label === 'manufacturer' || key === 'manufacturer' || key === 'vendor') return 'manufacturer'
  if (label === 'brand' || key === 'brand') return 'brand'
  if (label === 'price' || key === 'price') return 'price'
  return null
}

export function displayFilterGroupLabel(group: ShopifyFilterGroup) {
  if (isSpecFilterGroup(group) && normalizedStaticFilterKey(group) === 'type') {
    return 'Product Type'
  }
  const staticKey = normalizedStaticFilterKey(group)
  if (staticKey !== 'type') return group.label
  const label = group.label.trim().toLowerCase()
  const key = group.key.trim().toLowerCase()
  if (label === 'type' || key === 'type' || key === 'store_taxonomy' || key === 'taxonomy') {
    return 'Category'
  }
  return group.label
}

export function isCannedFilterGroup(group: ShopifyFilterGroup) {
  const key = group.key.trim().toLowerCase()
  const label = group.label.trim().toLowerCase()
  return CANNED_FILTER_KEYS.has(key) || CANNED_FILTER_LABELS.has(label)
}

export function usableUniqueFilterOptions(group: ShopifyFilterGroup) {
  const seen = new Set<string>()
  return (group.options ?? []).filter((option) => {
    const value = option.value.trim()
    const label = option.label.trim()
    if (!value || !label || seen.has(value)) return false
    seen.add(value)
    return true
  })
}

export function isRenderableFilterGroup(
  group: ShopifyFilterGroup | null | undefined,
): group is ShopifyFilterGroup {
  if (group == null) return false
  if (isCannedFilterGroup(group)) return false
  if (group.type === 'range') {
    return group.min != null || group.max != null || group.selected != null
  }
  return usableUniqueFilterOptions(group).length > 1
}

export function orderFilterGroups(groups: ShopifyFilterGroup[]) {
  return [...groups].sort((a, b) => {
    const aStatic = normalizedStaticFilterKey(a)
    const bStatic = normalizedStaticFilterKey(b)
    const aRank = aStatic ? STATIC_FILTER_ORDER.indexOf(aStatic) : Number.MAX_SAFE_INTEGER
    const bRank = bStatic ? STATIC_FILTER_ORDER.indexOf(bStatic) : Number.MAX_SAFE_INTEGER
    if (aRank !== bRank) return aRank - bRank
    if (isSpecFilterGroup(a) !== isSpecFilterGroup(b)) return isSpecFilterGroup(a) ? 1 : -1
    return a.label.localeCompare(b.label)
  })
}

export function filterGroupBadge(group: ShopifyFilterGroup) {
  if (isSpecFilterGroup(group)) return group.type === 'range' ? 'Spec range' : 'AI spec'
  if (group.type === 'range') return 'Range'
  return null
}

export function pickedFromStorefront(product: ShopifySearchProduct): PickedProduct {
  return {
    uuid: product.uuid,
    title: product.title || product.model,
    model: product.model,
    manufacturer: product.vendor.title,
    thumbnail: product.thumbnail,
    price: product.price,
    pricedOnEachQuote: false,
    boughtThrough: null,
    warehouseSource:
      product.quick_ship_listing_uuid && product.condition
        ? { condition: product.condition, used_grade: product.used_grade ?? null }
        : null,
  }
}

export function pickedFromQuotable(product: QuotableProduct): PickedProduct {
  return {
    uuid: product.uuid,
    title: product.store_title || product.specifications || product.model,
    model: product.model,
    manufacturer: product.manufacturer.name,
    thumbnail: getImageUrl(product.images),
    price: product.priced_on_each_quote ? null : (product.current_price?.store_price ?? null),
    pricedOnEachQuote: Boolean(product.priced_on_each_quote),
    boughtThrough: product.bought_through ?? null,
  }
}
